import hljs from 'highlight.js/lib/core';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Tabs,
  TabsList,
  TabsTrigger,
  cn,
} from '@column/ui';

import { NotificationList } from '~/components/NotificationList';
import { EventType, WebhookRepository, WebhookVerify } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

import { EventTypeCombobox } from './EventTypeCombobox';

type ResultTab = 'request' | 'response';

export interface WebhookResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  webhookId: string;
  isSandbox: boolean;
  webhookResult?: WebhookVerify;
  testableEvents: string[];
  testEventType?: string;
}

export const WebhookResultModal: FC<WebhookResultModalProps> = ({
  isOpen,
  onClose,
  webhookId,
  isSandbox,
  webhookResult: initialWebhookResult,
  testableEvents,
  testEventType: initialTestEventType,
}) => {
  const { addSuccessNotification, addDangerNotification, clearNotifications } = useNotificationStore();
  const currentUser = useSessionStore((state) => state.currentUser);

  const [testEventType, setTestEventType] = useState<string>(initialTestEventType || '');
  const [webhookResult, setWebhookResult] = useState<WebhookVerify | undefined>(initialWebhookResult);
  const [activeTab, setActiveTab] = useState<ResultTab>('request');
  const codeRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (isOpen) {
      setTestEventType(initialTestEventType || '');
      setWebhookResult(initialWebhookResult);
      setActiveTab('request');
      clearNotifications('popup');
    }
  }, [isOpen, initialTestEventType, initialWebhookResult, clearNotifications]);

  const highlightCode = (element: HTMLElement | null) => {
    if (element) {
      element.removeAttribute('data-highlighted');
      hljs.highlightElement(element);
    }
  };

  // The dialog content mounts after open, so highlight once the code element exists too.
  const codeRefCallback = (element: HTMLElement | null) => {
    codeRef.current = element;
    highlightCode(element);
  };

  useEffect(() => {
    highlightCode(codeRef.current);
  }, [webhookResult?.requestBody, webhookResult?.responseBody, activeTab]);

  const handleTestWebhook = useCallback(() => {
    if (!currentUser || !webhookId) {
      return;
    }
    if (!testEventType) {
      addDangerNotification({ content: 'Select an event type first', display: 'popup' });
      return;
    }
    WebhookRepository.verify(webhookId, { eventType: testEventType as EventType }, isSandbox)
      .then((response) => {
        setWebhookResult(response);
        if (response.success) {
          addSuccessNotification({
            content: `${testEventType} successfully sent`,
            display: 'popup',
          });
          return;
        }
        addDangerNotification({
          content: `${response.responseCode} Error`,
          display: 'popup',
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'popup',
        });
      });
  }, [currentUser, webhookId, testEventType, isSandbox, addSuccessNotification, addDangerNotification]);

  const responseCode = webhookResult?.responseCode;
  const isSuccess = typeof responseCode !== 'undefined' && responseCode >= 200 && responseCode < 300;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        overlayScroll
        showCloseButton={false}
        className="gap-0 overflow-hidden p-0 sm:max-w-3xl"
        onOpenAutoFocus={(event) => {
          // The header close button comes first in the DOM; start on the event type field instead.
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector('input')?.focus();
        }}
      >
        <DialogHeader className="flex-row items-start justify-between gap-4 border-b border-gray-100 px-6 py-4 dark:border-white/[0.08]">
          <DialogTitle>Verify Webhook</DialogTitle>
          <DialogDescription className="sr-only">
            Send a test event and inspect the request and response.
          </DialogDescription>
          <DialogClose asChild>
            {/* 24px hit area trimmed to the title's 20px line height so the icon centers on the text. */}
            <button
              type="button"
              aria-label="Close"
              className="-my-0.5 -mr-1 flex size-6 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-gray-100 hover:text-foreground dark:hover:bg-white/10"
            >
              <Icon.Close className="[--icon-size-x:16px] [--icon-size-y:16px]" />
            </button>
          </DialogClose>
        </DialogHeader>

        <div className="flex flex-col gap-3 px-6 pt-4">
          {/* Inside the dialog so errors stack above its overlay. */}
          <NotificationList display="popup" spacing="bottom" className="!m-0" />

          <div className="flex items-center gap-3">
            <EventTypeCombobox
              className="max-w-80 flex-1"
              value={testEventType}
              onValueChange={setTestEventType}
              eventTypes={testableEvents}
            />
            <Button type="button" variant="secondary" onClick={handleTestWebhook}>
              <Icon.Bell />
              Send
            </Button>
            {webhookResult && (
              <div className="ml-auto flex items-center gap-2 text-sm leading-5 font-medium text-foreground">
                <span className={cn('flex', isSuccess ? 'text-seafoam-600' : 'text-destructive')}>
                  {isSuccess ? (
                    <Icon.CircleCheck className="[--icon-color:currentColor]" />
                  ) : (
                    <Icon.CircleCross className="[--icon-color:currentColor]" />
                  )}
                </span>
                {responseCode} {isSuccess ? 'OK' : 'Error'}
              </div>
            )}
          </div>

          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as ResultTab)}>
            <TabsList variant="line" className="h-10">
              <TabsTrigger value="request">Request</TabsTrigger>
              <TabsTrigger value="response">Response</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="h-[560px] max-h-[min(560px,calc(100dvh-300px))] overflow-y-auto border-t border-gray-100 bg-gray-25 px-6 py-5 text-[13px] leading-[1.6] dark:border-white/[0.08] dark:bg-white/[0.02]">
          {/* The highlight.js theme stylesheet is unlayered, so its .hljs background and padding need !important. */}
          <pre className="m-0 p-0">
            <code
              ref={codeRefCallback}
              className="language-json font-[Roboto_Mono,monospace] break-words whitespace-pre-wrap [&.hljs]:bg-transparent! [&.hljs]:p-0!"
            >
              {(activeTab === 'request' ? webhookResult?.requestBody : webhookResult?.responseBody) ?? ''}
            </code>
          </pre>
        </div>
      </DialogContent>
    </Dialog>
  );
};
